import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { applyRotations } from "@coworker/apprestructured/src/task-management/util";
import { useTranslation } from "@coworker/locales";
import { useCallback, useState } from "react";
import { AddImagesModal } from "../AddImagesModal/AddImagesModal";
import { useNotifications } from "../Notifications/NotificationsContext";
import ImagesListViewItem from "./ListView/ImagesListViewItem";

type AddImagesProps = {
  images: NewOrExistingFile[];
  onConfirmClick: (images: NewOrExistingFile[]) => void;
  disabled: boolean;
};

export const AddImages = ({
  images,
  onConfirmClick,
  disabled,
}: AddImagesProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<NewOrExistingFile[]>([]);
  const { t } = useTranslation();
  const { showError } = useNotifications();

  const handleListViewItemClick = useCallback(() => {
    setModalImages(images);
    setIsModalOpen(true);
  }, [images]);

  const handleAddImagesModalConfirmClick = useCallback(async () => {
    try {
      const processedImages = await applyRotations(modalImages);
      await Promise.resolve(onConfirmClick(processedImages));
    } catch (error) {
      showError({
        title: t("imageUploadFailedTitleString"),
        text: t("imageUploadFailedTextString"),
      });
      console.error("Failed to confirm added images.", error);
    }
  }, [modalImages, onConfirmClick, showError, t]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <ImagesListViewItem
        images={images}
        onClick={handleListViewItemClick}
        disabled={disabled}
      />
      <AddImagesModal
        isOpen={isModalOpen}
        images={modalImages}
        onImagesChange={setModalImages}
        onClose={handleCloseModal}
        onConfirmClick={handleAddImagesModalConfirmClick}
      />
    </>
  );
};
